import { CONSTANTS } from "@/constants";
import { SearchState } from "@/contexts/SearchContext/interfaces";
import { Property } from "@/interfaces/property.interface";
import { SavedSearchAlert } from "@/interfaces/search-alert.interface";
import { sendGTMEvent } from "@next/third-parties/google";
import {
  propertiesToViewItemECommerce,
  propertyToTransactionEcommerce,
  propertyToViewEcommerce,
  stateToViewItemList,
} from "./helpers";

export enum GAEventType {
  VIEW_ITEM_LIST = "view_item_list",
  SELECT_ITEM = "select_item",
  VIEW_ITEM = "view_item",
  ADD_TO_WISHLIST = "add_to_wish_list",
  PURCHASE = "purchase",
  VIEW_PROMOTION = "view_promotion",
  SELECT_PROMOTION = "select_promotion",
  OUTBOUND_CLICK = "outbound_click",
  PROPERTY_SEARCH_CREATED = "property_search_created",
}

export type GAEvent = {
  event: GAEventType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & Record<string, any>;

export type GAPromotion = {
  creative_name: string;
  creative_slot: string;
  promotion_id: string;
  promotion_name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & Record<string, any>;

export const fireEvent = (event: GAEvent) => {
  if (CONSTANTS.IS_PRODUCTION) {
    console.debug("Firing GA event", event);
    sendGTMEvent(event);
  } else {
    console.debug("Firing GA event", event);
  }
};

// listId is basically from where it came from
export const gTagEvents = {
  viewPropertyList: (
    listId: string,
    properties: Property[],
    state: SearchState
  ) => {
    fireEvent({
      event: GAEventType.VIEW_ITEM_LIST,
      where: listId,
      ...stateToViewItemList(state),
      ecommerce: propertiesToViewItemECommerce(properties, listId),
    });
  },
  selectProperty: (
    property: Property,
    listId: string,
    state: SearchState,
    index: number
  ) => {
    const mappedEcommerce = propertiesToViewItemECommerce([property], listId);
    mappedEcommerce.items[0].index = index;

    fireEvent({
      event: GAEventType.SELECT_ITEM,
      where: listId,
      ...stateToViewItemList(state),
      ecommerce: mappedEcommerce,
    });
  },
  viewProperty: (property: Property, listId: string) => {
    fireEvent({
      event: GAEventType.VIEW_ITEM,
      where: listId,
      ecommerce: propertyToViewEcommerce(property, listId),
    });
  },
  addToWishList: (property: Property, listId: string) => {
    fireEvent({
      event: GAEventType.ADD_TO_WISHLIST,
      ecommerce: propertyToViewEcommerce(property, listId),
    });
  },
  whatsappClick: (
    property: Property,
    listId: string,
    transactionId: string
  ) => {
    fireEvent({
      event: GAEventType.PURCHASE,
      custom_event: "whatsapp",
      ecommerce: propertyToTransactionEcommerce(
        transactionId,
        property,
        listId
      ),
    });
  },
  callClick: (property: Property, listId: string, transactionId: string) => {
    fireEvent({
      event: GAEventType.PURCHASE,
      custom_event: "phone",
      ecommerce: propertyToTransactionEcommerce(
        transactionId,
        property,
        listId
      ),
    });
  },
  outboundClick: (location: string) => {
    fireEvent({
      event: GAEventType.OUTBOUND_CLICK,
      link_location: location,
    });
  },
  propertySearchCreated: (state: SearchState, alert: SavedSearchAlert) => {
    fireEvent({
      event: GAEventType.PROPERTY_SEARCH_CREATED,
      receive_update: alert.duration,
      ...stateToViewItemList(state),
    });
  },
  viewPromotion: (promotion: GAPromotion) => {
    fireEvent({
      event: GAEventType.VIEW_PROMOTION,
      ecommerce: promotion,
    });
  },
  selectPromotion: (promotion: GAPromotion) => {
    fireEvent({
      event: GAEventType.SELECT_PROMOTION,
      ecommerce: promotion,
    });
  },
};
